import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { QueryImage } from "types";
import { useHoverDelay } from "helpers/utility/utilities";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import styles from "./QueryImage.module.scss";

export interface QueryTabImageProps {
  image: QueryImage;
  className?: string;
  onHoverPreview?: () => void;
  hoverDelay?: number;
}

export default function QueryTabImage({
  image,
  className,
  onHoverPreview,
  hoverDelay = 500,
}: QueryTabImageProps) {
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);

  // Setup hover preview functionality
  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onHoverPreview || (() => {}),
    hoverDelay,
  );

  const createCroppedImage = useCallback(() => {
    if (!image || !image.boundingBox) {
      setCroppedImageUrl(null);
      return;
    }

    const [x, y, x1, y1] = image.boundingBox!;
    const width = x1 - x;
    const height = y1 - y;

    // If bounding box represents full image or very large crop, just use original
    if ((x === 0 && y === 0) || width > 1000 || height > 1000) {
      setCroppedImageUrl(image.uri);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
        const dataUrl = canvas.toDataURL();
        setCroppedImageUrl(dataUrl);
      } else {
        setCroppedImageUrl(image.uri);
      }
    };
    img.onerror = () => {
      setCroppedImageUrl(image.uri);
    };
    img.src = image.uri;
  }, [image]);

  useEffect(() => {
    createCroppedImage();
  }, [createCroppedImage]);

  if (!image) {
    return null;
  }

  const imageContent = image.boundingBox ? (
    <div
      className={classNames(styles.croppedImageContainer, className)}
      style={{
        aspectRatio: image.boundingBox
          ? `${image.boundingBox[2] - image.boundingBox[0]} / ${image.boundingBox[3] - image.boundingBox[1]}`
          : "1",
      }}
    >
      <img
        src={croppedImageUrl || image.uri}
        alt="Cropped query"
        className={styles.croppedImage}
      />
    </div>
  ) : (
    <img
      className={classNames(styles.fullImage, className)}
      src={image.uri}
      alt="Query"
    />
  );

  // Wrap with ScrollAwareHover if hover preview is enabled
  return onHoverPreview ? (
    <ScrollAwareHover
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {imageContent}
    </ScrollAwareHover>
  ) : (
    imageContent
  );
}
